<script setup lang="ts">
import useAccount from "@/composables/useAccount.ts";
import { Company } from "@/modules/companies/interfaces/company.ts";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { reactive } from "vue";
import { useToast } from "vue-toastification";

interface Props {
  companySelected?: Company;
  companies: Company[];
}

interface Emits {
  (e: "actionModalInviteUser"): void;
}

const props = defineProps<Props>();
const emits = defineEmits<Emits>();

const toast = useToast();

const { inviteUserAsync, inviteUserLoading } = useAccount();

const formIniviteUser = reactive({
  email: "",
  companyId: props.companySelected?.id || "",
  name: "",
});

const rules = {
  email: {
    required: helpers.withMessage("El correo es requerido", required),
    requiredIf: helpers.withMessage(
      "El correo debe ser válido",
      (value: string) => {
        const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
        return regex.test(value);
      },
    ),
  },
  companyId: {
    required: helpers.withMessage("La empresa es requerida", required),
  },
};

const v$ = useVuelidate(rules, formIniviteUser);

const handleInviteUser = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  await inviteUserAsync({
    params: {
      companyId: formIniviteUser.companyId!,
      email: formIniviteUser.email,
      name: formIniviteUser.name,
    },
    config: {
      raiseToast: true,
      onSuccessToast: "Se ha enviado la invitación al usuario correctamente.",
      onErrorToast: "No se ha podido enviar la invitación al usuario.",
    },
  });

  emits("actionModalInviteUser");
  formIniviteUser.email = "";
  formIniviteUser.name = "";
};
</script>

<template>
  <form @submit.prevent="handleInviteUser" class="flex flex-col gap-4">
    <!-- Empresa -->
    <div v-if="!companySelected" class="flex flex-col gap-1">
      <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        Empresa <span class="text-red-500">*</span>
      </label>
      <div class="relative">
        <select
          v-model="formIniviteUser.companyId"
          class="w-full text-sm rounded-lg px-3.5 py-2.5 border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#021F58]/25 focus:border-[#021F58] appearance-none pr-9 transition-all text-gray-700"
        >
          <option value="" disabled>Selecciona una empresa</option>
          <option
            v-for="company in companies"
            :key="company.id"
            :value="company.id"
          >{{ company.name }}</option>
        </select>
        <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center">
          <span class="material-symbols-outlined text-gray-400 text-[18px]">expand_more</span>
        </span>
      </div>
    </div>

    <!-- Nombre del invitado -->
    <div class="flex flex-col gap-1">
      <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        Nombre del invitado
      </label>
      <input
        v-model="formIniviteUser.name"
        type="text"
        placeholder="Nombre del invitado"
        class="w-full text-sm rounded-lg px-3.5 py-2.5 border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#021F58]/25 focus:border-[#021F58] placeholder-gray-300 transition-all"
      />
    </div>

    <!-- Correo -->
    <div class="flex flex-col gap-1">
      <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">
        Correo electrónico <span class="text-red-500">*</span>
      </label>
      <div class="relative">
        <span class="absolute inset-y-0 left-3.5 flex items-center pointer-events-none">
          <span class="material-symbols-outlined text-gray-400 text-[18px]">mail</span>
        </span>
        <input
          v-model="formIniviteUser.email"
          type="email"
          placeholder="usuario@empresa.com"
          class="w-full text-sm rounded-lg pl-10 pr-3.5 py-2.5 border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#021F58]/25 focus:border-[#021F58] placeholder-gray-300 transition-all"
        />
      </div>
    </div>

    <!-- Submit -->
    <div class="flex justify-end pt-1">
      <button
        type="submit"
        :disabled="inviteUserLoading"
        class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#021F58] text-white text-sm font-semibold hover:bg-[#1C3F7A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
      >
        <div v-if="inviteUserLoading" class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
        <span v-else class="material-symbols-outlined text-base">send</span>
        Enviar invitación
      </button>
    </div>
  </form>
</template>
